import React, { JSX } from 'react';
import {
    Edit,
    TabbedForm,
    SelectInput,
    DateField,
    TextField,
    FunctionField,
    WithRecord,
    SelectField
} from 'react-admin';
import {
    Box,
    Stack
} from '@mui/material';
import { ContactForm } from "../../types/entities";
import { ConversationPanel } from "./components/Sales/ConversationPanel";
import { ChatFieldBridge } from "../chat-bot/components/ChatFieldBridge";

const formReasons: { id: string, name: string }[] = [
    { id: 'general', name: 'General' },
    { id: 'walkthrough', name: 'Book Walkthrough' },
    { id: 'enterprise', name: 'Enterprise Request' },
    { id: 'integration', name: 'Custom Integration' },
    { id: 'reseller', name: 'Reseller' },
];

const formStatus: { id: string, name: string }[] = [
    { id: 'new', name: 'New' },
    { id: 'active', name: 'Active Conversation' },
    { id: 'dead', name: 'Failed to convert' },
    { id: 'customer', name: 'Converted to Customer' },
    { id: 'closed', name: 'Closed' },
];

/**
 * The enquiry and the conversation are two tabs rather than two columns (user, 2026-09-08).
 *
 * Side by side they crowded each other: the submitted message wants to be read at width, and
 * replying wants the room to see the thread and type into it. Nothing here is needed while
 * answering except the message itself, which the thread tab repeats at the top anyway.
 */
export const ContactFormEdit: () => JSX.Element = (): JSX.Element => {
    return (
        <Edit>
            <TabbedForm>
                <TabbedForm.Tab label="Enquiry">
                    <Stack spacing={ 2 } sx={ { width: '100%', maxWidth: 900 } }>
                        <Box sx={ { width: '100%', display: 'flex', justifyContent: 'space-between' } } >
                            <span>{ "Status" }</span>
                            <SelectInput
                                source="status"
                                name="status"
                                choices={ formStatus }
                                sx={ { width: '75%' } }
                            />
                        </Box>
                        <Box sx={ { width: '100%', display: 'flex', justifyContent: 'space-between' } } >
                            <span>{ "Name" }</span>
                            <FunctionField
                                label="Name"
                                render={ (record: ContactForm): string => `${record.firstname} ${record.lastname}` }
                            />
                        </Box>
                        <Box sx={ { width: '100%', display: 'flex', justifyContent: 'space-between' } } >
                            <span>{ "Email" }</span>
                            <TextField source="email" />
                        </Box>
                        <Box sx={ { width: '100%', display: 'flex', justifyContent: 'space-between' } } >
                            <span>{ "Created At" }</span>
                            <DateField source="createdAt" showTime />
                        </Box>
                        <Box sx={ { width: '100%', display: 'flex', justifyContent: 'space-between' } } >
                            <span>{ "Subject" }</span>
                            <SelectField source="type" choices={ formReasons } />
                        </Box>
                        <Box sx={ { width: '100%', display: 'flex', justifyContent: 'space-between' } } >
                            <span>{ "Message" }</span>
                            <WithRecord label=" " render={
                                (record: ContactForm): JSX.Element => (
                                    <textarea style={ {
                                        width: '75%',
                                        height: '400px',
                                        resize: 'none',
                                        padding: '5px',
                                    } } readOnly value={ record.message } />
                                )
                            } />
                        </Box>
                    </Stack>
                    {/* Inside a tab, not beside them: TabbedFormTabs clones every direct child into the
                        tab bar. One tab is enough - react-admin mounts them all and hides the
                        inactive ones, so the whole form is registered from here. */}
                    <ChatFieldBridge />
                </TabbedForm.Tab>

                <TabbedForm.Tab label="Conversation">
                    { /* The old notes editor lived here. Notes now carry an author and a
                         timestamp, and the JSON column behind it is rendered read-only inside the
                         panel as earlier notes, so nothing already written is lost. */ }
                    <WithRecord label=" " render={
                        (record: ContactForm): JSX.Element => (
                            <ConversationPanel
                                subject={ { contactForm: Number(record.id) } }
                                defaultSubject="Your NokNok enquiry"
                            />
                        )
                    } />
                </TabbedForm.Tab>
            </TabbedForm>
        </Edit>
    );
}
